import { storageKey, createScopedStorage, readLocalJson, writeLocalJson, removeLocalKey } from '@authe/fe-commons/storage';
import { EventResourceType, resourceLabel } from '@authe/fe-commons/events';
export { EventResourceType } from '@authe/fe-commons/events';
import { createLogger } from '@authe/fe-commons/logging';
import { unwrapData } from '@authe/fe-commons/api';
import { isWidenableScope, ScopeOptionSchema } from '@authe/fe-commons/models';
import { num, asString } from '@authe/fe-commons/display';
import { BUNDLE_KEY, readGlassBoxProvenance } from '@authe/fe-commons/attribution';
import { TagList, ChainAvatar, RiskBadge, DEFAULT_DEBOUNCE_MS, useDebounced, EntityCard, AddressDisplay, HashDisplay, Badge, cn, HoverCard, HoverCardTrigger, HoverCardContent, FilterChip, CommandGroup, CommandItem, Dialog, DialogContent, DialogTitle, DialogDescription, Command, CommandInput, CommandList, Skeleton, CommandEmpty, CommandSeparator, Popover, PopoverTrigger, Button, PopoverContent, AttributionChips } from '@authe/ui-core';
import { catalogBundleChips } from '@authe/fe-commons/overlays';
import { riskLevelFromScore } from '@authe/fe-commons/precedence';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as React2 from 'react';
import { useDepsChanged } from '@authe/ui-core/api';
import { addressRowScopeLabel } from '@authe/fe-commons/addresses';

// src/config.ts
var SEARCH_PACKAGE = "search";
var SEARCH_RECENTS_KEY = storageKey(SEARCH_PACKAGE, "recents");
var SEARCH_RECENTS_NAME = `${SEARCH_PACKAGE}:recents`;
var SEARCH_MIN_QUERY_LENGTH = 2;
var SEARCH_RECENTS_LIMIT = 8;
var SEARCH_DEFAULT_LIMIT = 20;
var SEARCH_TENANT_KIND = "tenant";
var SEARCH_RESOURCE_KINDS = [
  EventResourceType.TRANSACTION,
  EventResourceType.ADDRESS,
  EventResourceType.ENTITY,
  EventResourceType.LABEL,
  EventResourceType.TAG,
  EventResourceType.GROUP,
  EventResourceType.USER,
  SEARCH_TENANT_KIND,
  EventResourceType.ORGANIZATION
];
function resourceKindLabel(kind) {
  if (kind === SEARCH_TENANT_KIND) return "Tenant";
  return resourceLabel(kind);
}
function resourceKindOrder(kind) {
  const idx = SEARCH_RESOURCE_KINDS.indexOf(kind);
  return idx === -1 ? SEARCH_RESOURCE_KINDS.length : idx;
}

// src/model/result.ts
function metadataString(hit, key) {
  const value = hit.metadata?.[key];
  return typeof value === "string" && value ? value : void 0;
}
function hitAddress(hit) {
  return metadataString(hit, "address") ?? (hit.id || void 0);
}
function hitTxHash(hit) {
  const fromMetadata = metadataString(hit, "tx_hash");
  if (fromMetadata) return fromMetadata;
  return /^0x[0-9a-fA-F]{64}$/.test(hit.id) ? hit.id : void 0;
}
function hitChains(hit) {
  const chains = hit.enrichment?.chains;
  if (chains && chains.length) return chains;
  return hit.chainId ? [hit.chainId] : [];
}
function chainScopedHit(hit, chainId) {
  return { ...hit, chainId, enrichment: { ...hit.enrichment, chains: [chainId] } };
}
function hitScopes(hit) {
  return hit.enrichment?.scopes ?? [];
}
function scopedHit(hit, scope) {
  return {
    ...hit,
    enrichment: {
      ...hit.enrichment,
      scope: scope.scope,
      scopes: hitScopes(hit).filter((option) => option.scope !== scope.scope)
    }
  };
}
function groupHitsByKind(hits) {
  const byKind = /* @__PURE__ */ new Map();
  for (const hit of hits) {
    const bucket = byKind.get(hit.kind);
    if (bucket) bucket.push(hit);
    else byKind.set(hit.kind, [hit]);
  }
  return [...byKind.entries()].sort((a, b) => resourceKindOrder(a[0]) - resourceKindOrder(b[0])).map(([kind, kindHits]) => ({ kind, label: resourceKindLabel(kind), hits: kindHits }));
}
function byRelevance(a, b) {
  const sa = a.score ?? 0;
  const sb = b.score ?? 0;
  if (sa !== sb) return sb - sa;
  return a.title.localeCompare(b.title);
}
var log = createLogger(`${SEARCH_PACKAGE}:provider`);
function servesAnyKind(provider, kinds) {
  if (!provider.kinds || !kinds || kinds.length === 0) return true;
  return kinds.some((k) => provider.kinds.includes(k));
}
function mergeFacets(facets) {
  const byField = /* @__PURE__ */ new Map();
  for (const facet of facets) {
    const existing = byField.get(facet.field);
    if (!existing) {
      byField.set(facet.field, { ...facet, values: facet.values.map((v) => ({ ...v })) });
      continue;
    }
    for (const value of facet.values) {
      const match = existing.values.find((v) => v.value === value.value);
      if (match) match.count = (match.count ?? 0) + (value.count ?? 0);
      else existing.values.push({ ...value });
    }
  }
  return [...byField.values()];
}
function createCompositeSearchProvider(providers, options = {}) {
  const id = options.id ?? "composite";
  const suggesters = providers.filter((p) => typeof p.suggest === "function");
  const composite = {
    id,
    query: async (query, ctx) => {
      const targets = providers.filter((p) => servesAnyKind(p, query.kinds));
      const settled = await Promise.allSettled(targets.map((p) => p.query(query, ctx)));
      const hits = [];
      const seen = /* @__PURE__ */ new Set();
      const facets = [];
      let total = 0;
      let sawTotal = false;
      let nextCursor = null;
      settled.forEach((result, i) => {
        if (result.status === "rejected") {
          log.warn("search provider failed", { provider: targets[i]?.id, error: String(result.reason) });
          return;
        }
        const res = result.value;
        for (const hit of res.hits) {
          const dedupeKey = `${hit.kind}:${hit.id}`;
          if (seen.has(dedupeKey)) continue;
          seen.add(dedupeKey);
          hits.push(hit);
        }
        if (res.facets) facets.push(...res.facets);
        if (typeof res.total === "number") {
          total += res.total;
          sawTotal = true;
        }
        if (nextCursor === null && res.nextCursor) nextCursor = res.nextCursor;
      });
      hits.sort(byRelevance);
      const limit = query.limit ?? SEARCH_DEFAULT_LIMIT;
      return {
        hits: hits.slice(0, limit),
        facets: facets.length ? mergeFacets(facets) : void 0,
        nextCursor,
        total: sawTotal ? total : null
      };
    }
  };
  if (suggesters.length) {
    composite.suggest = async (text, ctx) => {
      const settled = await Promise.allSettled(suggesters.map((p) => p.suggest(text, ctx)));
      const out = /* @__PURE__ */ new Set();
      for (const result of settled) {
        if (result.status === "fulfilled") for (const s of result.value) out.add(s);
      }
      return [...out];
    };
  }
  return composite;
}
function createInMemorySearchProvider(seed, options = {}) {
  const id = options.id ?? "in-memory";
  return {
    id,
    query: async (query) => {
      const text = query.text.trim().toLowerCase();
      const kinds = query.kinds && query.kinds.length ? new Set(query.kinds) : null;
      let matches = seed.filter((hit) => {
        if (kinds && !kinds.has(hit.kind)) return false;
        if (query.chainId && hit.chainId && hit.chainId !== query.chainId) return false;
        if (!text) return true;
        return hit.id.toLowerCase().includes(text) || hit.title.toLowerCase().includes(text) || (hit.subtitle?.toLowerCase().includes(text) ?? false);
      });
      matches = matches.sort(byRelevance);
      const counts = /* @__PURE__ */ new Map();
      for (const hit of matches) counts.set(hit.kind, (counts.get(hit.kind) ?? 0) + 1);
      const kindFacet = {
        field: "kind",
        label: "Type",
        values: SEARCH_RESOURCE_KINDS.filter((k) => counts.has(k)).map((k) => ({
          value: k,
          label: resourceKindLabel(k),
          count: counts.get(k)
        }))
      };
      const limit = query.limit ?? SEARCH_DEFAULT_LIMIT;
      return { hits: matches.slice(0, limit), facets: [kindFacet], total: matches.length };
    },
    suggest: async (text) => {
      const t = text.trim().toLowerCase();
      if (!t) return [];
      const out = /* @__PURE__ */ new Set();
      for (const hit of seed) {
        if (hit.title.toLowerCase().includes(t)) out.add(hit.title);
        if (out.size >= 8) break;
      }
      return [...out];
    }
  };
}
var asRecord = (value) => value && typeof value === "object" ? value : {};
var asArray = (value) => Array.isArray(value) ? value : [];
function toSearchRequestBody(query) {
  return {
    // Trimmed: the backend classifies + validates on the stripped text anyway,
    // and sending the raw value makes two spellings of one query miss each
    // other's cache entry on the read lane.
    text: query.text.trim(),
    ...query.kinds && query.kinds.length ? { kinds: query.kinds } : {},
    ...query.chainId ? { chain_id: query.chainId } : {},
    ...typeof query.limit === "number" ? { limit: query.limit } : {},
    ...query.cursor ? { cursor: query.cursor } : {}
  };
}
function mapGlyph(raw) {
  return { logo: asString(raw.logo), color: asString(raw.color) };
}
function mapLabel(raw) {
  return {
    ...readGlassBoxProvenance(raw),
    ...mapGlyph(raw),
    label: asString(raw.label) ?? "",
    value: asString(raw.value),
    scope: asString(raw.scope),
    // Labels carry a severity as well as tags: `Sanctioned: OFAC` is a label.
    severity: asString(raw.severity)
  };
}
function mapTag(raw) {
  return {
    ...readGlassBoxProvenance(raw),
    ...mapGlyph(raw),
    label: asString(raw.label) ?? "",
    severity: asString(raw.severity)
  };
}
function mapEntity(raw) {
  return {
    ...readGlassBoxProvenance(raw),
    ...mapGlyph(raw),
    id: asString(raw.id) ?? "",
    name: asString(raw.name) ?? "",
    entityType: asString(raw.entity_type),
    isPrimary: raw.is_primary === true
  };
}
function mapGroup(raw) {
  return {
    ...readGlassBoxProvenance(raw),
    ...mapGlyph(raw),
    id: asString(raw.id) ?? "",
    name: asString(raw.name),
    count: num(raw.count)
  };
}
function mapScope(raw) {
  const parsed = ScopeOptionSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
function mapEnrichment(value) {
  if (!value || typeof value !== "object") return void 0;
  const raw = value;
  return {
    name: asString(raw.name),
    chains: Array.isArray(raw.chains) ? raw.chains.filter((c) => typeof c === "string") : void 0,
    labels: asArray(raw.labels).map(mapLabel),
    tags: asArray(raw.tags).map(mapTag),
    entity: raw.entity ? mapEntity(asRecord(raw.entity)) : null,
    groups: asArray(raw.groups).map(mapGroup),
    riskLevel: asString(raw.risk_level),
    riskScore: num(raw.risk_score),
    logoUrl: asString(raw.logo_url),
    scope: asString(raw.scope),
    scopes: asArray(raw.scopes).map(mapScope).filter((scope) => scope !== null),
    extra: raw.extra && typeof raw.extra === "object" ? raw.extra : void 0
  };
}
function mapHit(raw) {
  const id = asString(raw.id);
  const kind = asString(raw.kind);
  if (!id || !kind) return null;
  return {
    id,
    kind,
    title: asString(raw.title) ?? id,
    subtitle: asString(raw.subtitle),
    chainId: asString(raw.chain_id) ?? null,
    score: num(raw.score),
    href: asString(raw.href),
    enrichment: mapEnrichment(raw.enrichment),
    metadata: raw.metadata && typeof raw.metadata === "object" ? raw.metadata : void 0
  };
}
function mapFacet(raw) {
  return {
    field: asString(raw.field) ?? "",
    label: asString(raw.label) ?? "",
    values: asArray(raw.values).map((v) => ({
      value: asString(v.value) ?? "",
      label: asString(v.label) ?? "",
      count: num(v.count)
    }))
  };
}
function fromSearchResponseDto(dto) {
  const raw = asRecord(dto);
  return {
    hits: asArray(raw.hits).map(mapHit).filter((hit) => hit !== null),
    facets: asArray(raw.facets).map(mapFacet),
    nextCursor: asString(raw.next_cursor) ?? null,
    total: num(raw.total) ?? null
  };
}

// src/provider/apiSearchProvider.ts
function createApiSearchProvider(client, options = {}) {
  const id = options.id ?? "api";
  const path = options.path ?? "/search";
  return {
    id,
    kinds: options.kinds,
    async query(query, ctx) {
      const dto = unwrapData(
        await client.post(path, toSearchRequestBody(query), {
          signal: ctx?.signal,
          cacheTtlMs: 0
        })
      );
      return fromSearchResponseDto(dto);
    },
    ...options.suggestPath ? {
      async suggest(text, ctx) {
        return unwrapData(
          await client.get(options.suggestPath, {
            query: { q: text },
            signal: ctx?.signal
          })
        ) ?? [];
      }
    } : {}
  };
}
var SEARCH_PREVIEW_FIELDS = [
  "chain",
  "risk",
  "entity",
  "labels",
  "tags",
  "groups",
  "name"
];
var DEFAULT_SEARCH_PREVIEW_CONFIG = {
  density: "compact",
  hover: true,
  defaultFields: [
    { field: "chain" },
    { field: "labels", max: 2 },
    { field: "tags", max: 2, detailOnly: true }
  ],
  fieldsByKind: {
    [EventResourceType.TRANSACTION]: [
      { field: "chain" },
      { field: "risk" },
      { field: "entity" },
      { field: "labels", max: 2 },
      { field: "tags", max: 3, detailOnly: true },
      { field: "groups", max: 2, detailOnly: true }
    ],
    [EventResourceType.ADDRESS]: [
      { field: "chain" },
      { field: "risk" },
      { field: "entity" },
      { field: "labels", max: 3 },
      { field: "tags", max: 3, detailOnly: true },
      { field: "groups", max: 2, detailOnly: true }
    ],
    [EventResourceType.ENTITY]: [
      { field: "labels", max: 3 },
      { field: "tags", max: 3, detailOnly: true }
    ],
    [EventResourceType.GROUP]: [
      { field: "labels", max: 2 },
      { field: "tags", max: 2, detailOnly: true }
    ],
    // Management resources have no chain/catalog context — just the display
    // name (the hit row already shows the title + a slug/email subtitle).
    [EventResourceType.USER]: [{ field: "name" }],
    [EventResourceType.ORGANIZATION]: [{ field: "name" }],
    [SEARCH_TENANT_KIND]: [{ field: "name" }]
  }
};
function resolveFields(config, kind) {
  return config.fieldsByKind[kind] ?? config.defaultFields;
}
var SEARCH_PREVIEW_FIELD_LABELS = {
  chain: "Chain",
  risk: "Risk",
  entity: "Entity",
  labels: "Labels",
  tags: "Tags",
  groups: "Groups",
  name: "Name"
};
function previewFieldLabel(field) {
  return SEARCH_PREVIEW_FIELD_LABELS[field] ?? field.replace(/^\w/, (c) => c.toUpperCase());
}
function attributionRenderer(kind, bundleFor, defaultMax) {
  const AttributionField = ({ hit, field }) => {
    const bundle = bundleFor(hit);
    if (!bundle[BUNDLE_KEY[kind]]?.length) return null;
    return /* @__PURE__ */ jsx(
      AttributionChips,
      {
        bundle,
        kinds: [kind],
        max: field.max ?? defaultMax,
        popoverTitle: kind === "entity" ? "Entities" : `${kind[0].toUpperCase()}${kind.slice(1)}s`
      }
    );
  };
  AttributionField.displayName = `AttributionField(${kind})`;
  return AttributionField;
}
var renderChain = ({ hit, field }) => {
  const chains = hitChains(hit);
  if (!chains.length) return null;
  return /* @__PURE__ */ jsx(
    TagList,
    {
      max: field.max ?? 2,
      popoverTitle: "Chains",
      items: chains.map((c) => /* @__PURE__ */ jsx(ChainAvatar, { chain: c, size: "sm", showName: "sr-only" }, c))
    }
  );
};
var renderRisk = ({ hit }) => {
  const e = hit.enrichment;
  if (!e) return null;
  const level = e.riskLevel ?? riskLevelFromScore(e.riskScore);
  if (!level) return null;
  return /* @__PURE__ */ jsx(RiskBadge, { level, score: e.riskScore });
};
var renderName = ({ hit }) => {
  const name = hit.enrichment?.name;
  if (!name) return null;
  return /* @__PURE__ */ jsx("span", { className: "truncate text-xs text-muted-foreground", children: name });
};
var renderEntity = attributionRenderer(
  "entity",
  (hit) => catalogBundleChips({ entity: hit.enrichment?.entity }),
  1
);
var renderLabels = attributionRenderer(
  "label",
  (hit) => catalogBundleChips({ labels: hit.enrichment?.labels }),
  3
);
var renderTags = attributionRenderer(
  "tag",
  (hit) => catalogBundleChips({ tags: hit.enrichment?.tags }),
  3
);
var renderGroups = attributionRenderer(
  "group",
  (hit) => catalogBundleChips({ groups: hit.enrichment?.groups }),
  2
);
var DEFAULT_FIELD_RENDERERS = {
  chain: renderChain,
  risk: renderRisk,
  entity: renderEntity,
  name: renderName,
  labels: renderLabels,
  tags: renderTags,
  groups: renderGroups
};
var FALLBACK = {
  config: DEFAULT_SEARCH_PREVIEW_CONFIG,
  renderers: DEFAULT_FIELD_RENDERERS
};
var SearchPreviewContext = React2.createContext(null);
function SearchPreviewProvider({
  config,
  renderers,
  children
}) {
  const value = React2.useMemo(
    () => ({
      config: config ?? DEFAULT_SEARCH_PREVIEW_CONFIG,
      renderers: renderers ? { ...DEFAULT_FIELD_RENDERERS, ...renderers } : DEFAULT_FIELD_RENDERERS
    }),
    [config, renderers]
  );
  return /* @__PURE__ */ jsx(SearchPreviewContext.Provider, { value, children });
}
function useSearchPreview() {
  return React2.useContext(SearchPreviewContext) ?? FALLBACK;
}
var log2 = createLogger(`${SEARCH_PACKAGE}:use-search`);
var NO_SEARCH = {
  status: "idle",
  hits: [],
  facets: [],
  total: null,
  nextCursor: null,
  error: null
};
function useSearch(options) {
  const {
    provider,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minLength = SEARCH_MIN_QUERY_LENGTH,
    initialText = "",
    initialKinds = [],
    chainId = null,
    limit = SEARCH_DEFAULT_LIMIT,
    autoSearch = true
  } = options;
  const [text, setText] = React2.useState(initialText);
  const [kinds, setKinds] = React2.useState(initialKinds);
  const [outcome, setOutcome] = React2.useState(NO_SEARCH);
  const runRef = React2.useRef(0);
  const manualRef = React2.useRef(null);
  const execute = React2.useCallback(
    async (query, signal) => {
      const runId = ++runRef.current;
      setOutcome((prev) => ({ ...prev, status: "searching", error: null }));
      try {
        const res = await provider.query(query, { signal });
        if (runId !== runRef.current || signal.aborted) return;
        setOutcome({
          status: "ready",
          hits: res.hits,
          facets: res.facets ?? [],
          total: res.total ?? null,
          nextCursor: res.nextCursor ?? null,
          error: null
        });
      } catch (err) {
        if (signal.aborted || runId !== runRef.current) return;
        log2.warn("search failed", { text: query.text, error: String(err) });
        setOutcome({
          ...NO_SEARCH,
          status: "error",
          error: err instanceof Error ? err : new Error(String(err))
        });
      }
    },
    [provider]
  );
  const searchable = text.trim().length >= minLength;
  if (!searchable && outcome !== NO_SEARCH) setOutcome(NO_SEARCH);
  const debouncedText = useDebounced(text, debounceMs);
  const settled = debouncedText === text;
  React2.useEffect(() => {
    if (!autoSearch || !settled || !searchable) return void 0;
    const controller = new AbortController();
    void execute({ text, kinds, chainId, limit }, controller.signal);
    return () => controller.abort();
  }, [settled, searchable, text, kinds, chainId, limit, autoSearch, execute]);
  React2.useEffect(() => {
    if (searchable) return;
    runRef.current += 1;
    manualRef.current?.abort();
  }, [searchable]);
  React2.useEffect(() => () => manualRef.current?.abort(), []);
  const toggleKind = React2.useCallback((kind) => {
    setKinds((prev) => prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]);
  }, []);
  const search = React2.useCallback(
    (override) => {
      manualRef.current?.abort();
      const controller = new AbortController();
      manualRef.current = controller;
      void execute({ text, kinds, chainId, limit, ...override }, controller.signal);
    },
    [execute, text, kinds, chainId, limit]
  );
  const clear = React2.useCallback(() => {
    manualRef.current?.abort();
    runRef.current += 1;
    setText("");
    setKinds([]);
    setOutcome(NO_SEARCH);
  }, []);
  const groups = React2.useMemo(() => groupHitsByKind(outcome.hits), [outcome.hits]);
  return { ...outcome, text, setText, kinds, setKinds, toggleKind, groups, search, clear };
}
function nextRecents(current, text, limit, now = Date.now()) {
  const trimmed = text.trim();
  if (!trimmed) return current;
  const without = current.filter((r) => r.text.toLowerCase() !== trimmed.toLowerCase());
  return [{ text: trimmed, at: now }, ...without].slice(0, Math.max(0, limit));
}
function withoutRecent(current, text) {
  const trimmed = text.trim().toLowerCase();
  return current.filter((r) => r.text.toLowerCase() !== trimmed);
}
function useRecentSearches(options = {}) {
  const { identity, limit = SEARCH_RECENTS_LIMIT } = options;
  const identityKey = identity ? [identity.app, identity.customerId, identity.orgId, identity.tenantId, identity.userId].join("|") : "";
  const read = React2.useCallback(() => {
    if (identity) return createScopedStorage(identity).get(SEARCH_RECENTS_NAME, []);
    return readLocalJson(SEARCH_RECENTS_KEY, []);
  }, [identityKey]);
  const write = React2.useCallback(
    (next) => {
      if (identity) createScopedStorage(identity).set(SEARCH_RECENTS_NAME, next);
      else writeLocalJson(SEARCH_RECENTS_KEY, next);
    },
    // `identity` is a fresh object literal on most renders; `identityKey` is the
    // value that actually decides which store this reads and writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identityKey]
  );
  const [recents, setRecents] = React2.useState(read);
  if (useDepsChanged([identityKey])) setRecents(read());
  const commit = React2.useCallback(
    (next) => {
      write(next);
      setRecents(next);
    },
    [write]
  );
  const add = React2.useCallback(
    (text) => {
      const next = nextRecents(recents, text, limit);
      if (next !== recents) commit(next);
    },
    [commit, recents, limit]
  );
  const remove = React2.useCallback(
    (text) => {
      commit(withoutRecent(recents, text));
    },
    [commit, recents]
  );
  const clear = React2.useCallback(() => {
    if (identity) createScopedStorage(identity).remove(SEARCH_RECENTS_NAME);
    else removeLocalKey(SEARCH_RECENTS_KEY);
    setRecents([]);
  }, [identityKey]);
  return { recents, add, remove, clear };
}
function SearchHitDetails({ hit, config, renderers }) {
  const e = hit.enrichment;
  if (hit.kind === EventResourceType.ENTITY) {
    return /* @__PURE__ */ jsx(
      EntityCard,
      {
        name: e?.name ?? hit.title,
        entityType: e?.entity?.entityType,
        avatarUrl: e?.logoUrl ?? e?.entity?.logo,
        description: hit.subtitle,
        tags: (e?.tags ?? []).map((t) => t.label)
      }
    );
  }
  const primaryChain = e?.chains?.[0] ?? hit.chainId ?? void 0;
  const fields = resolveFields(config, hit.kind);
  const address = hit.kind === EventResourceType.ADDRESS ? hitAddress(hit) : void 0;
  const txHash = hit.kind === EventResourceType.TRANSACTION ? hitTxHash(hit) : void 0;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx("span", { className: "text-[11px] font-medium uppercase tracking-wide text-muted-foreground", children: resourceKindLabel(hit.kind) }),
    address ? /* @__PURE__ */ jsx(AddressDisplay, { address, chain: primaryChain, label: e?.name, showExplorerIcon: true }) : txHash ? (
      // `hit.id` is this tx's route id — a content-addressed canonical id for
      // an indexed tx — which is exactly the focus id `HashDisplay` wants.
      /* @__PURE__ */ jsx(HashDisplay, { hash: txHash, chain: primaryChain, canonicalTxId: hit.id, showExplorerIcon: true })
    ) : /* @__PURE__ */ jsx("div", { className: "text-sm font-medium", children: e?.name ?? hit.title }),
    hit.subtitle && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: hit.subtitle }),
    /* @__PURE__ */ jsx("div", { className: "space-y-1.5", children: fields.map((f) => {
      const node = renderers[f.field]?.({ hit, field: f, density: "detailed" });
      if (!node) return null;
      return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "w-14 shrink-0 pt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground", children: previewFieldLabel(f.field) }),
        /* @__PURE__ */ jsx("span", { className: "flex flex-wrap items-center gap-1", children: node })
      ] }, f.field);
    }) })
  ] });
}
var MONO_KINDS = /* @__PURE__ */ new Set([EventResourceType.TRANSACTION, EventResourceType.ADDRESS]);
function SearchHitItem({ hit, config: configProp, className }) {
  const ctx = useSearchPreview();
  const config = configProp ?? ctx.config;
  const renderers = ctx.renderers;
  const mono = MONO_KINDS.has(hit.kind);
  const fields = resolveFields(config, hit.kind);
  const inlineFields = config.density === "detailed" ? fields : fields.filter((f) => !f.detailOnly);
  const previews = inlineFields.map((f) => {
    const node = renderers[f.field]?.({ hit, field: f, density: config.density });
    return node ? /* @__PURE__ */ jsx(React2.Fragment, { children: node }, f.field) : null;
  }).filter(Boolean);
  const row = /* @__PURE__ */ jsxs("div", { className: cn("flex w-full items-center gap-2", className), children: [
    /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "shrink-0", children: resourceKindLabel(hit.kind) }),
    /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col", children: [
      /* @__PURE__ */ jsx("span", { className: cn("truncate", mono && "font-mono text-xs"), children: hit.title }),
      hit.subtitle && /* @__PURE__ */ jsx("span", { className: "truncate text-xs text-muted-foreground", children: hit.subtitle })
    ] }),
    previews.length > 0 && /* @__PURE__ */ jsx("span", { className: "ml-auto flex shrink-0 items-center gap-1.5", children: previews })
  ] });
  if (config.hover === false || !hit.enrichment) return row;
  return /* @__PURE__ */ jsxs(HoverCard, { openDelay: 140, closeDelay: 60, children: [
    /* @__PURE__ */ jsx(HoverCardTrigger, { asChild: true, children: row }),
    /* @__PURE__ */ jsx(HoverCardContent, { align: "start", className: "w-80", children: /* @__PURE__ */ jsx(SearchHitDetails, { hit, config, renderers }) })
  ] });
}
function FacetFilters({
  selected,
  onToggle,
  kinds = SEARCH_RESOURCE_KINDS,
  counts,
  className
}) {
  const active = new Set(selected);
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "group",
      "aria-label": "Filter by resource type",
      className: cn("flex flex-wrap items-center gap-1.5 border-b px-3 py-2", className),
      children: kinds.map((kind) => /* @__PURE__ */ jsx(
        FilterChip,
        {
          active: active.has(kind),
          count: counts?.[kind],
          keepFocus: true,
          onClick: () => onToggle(kind),
          children: resourceKindLabel(kind)
        },
        kind
      ))
    }
  );
}
function SearchResults({
  groups,
  onSelect,
  itemConfig,
  chainDrilldown = false,
  scopeDrilldown = false
}) {
  const [expanded, setExpanded] = React2.useState(() => /* @__PURE__ */ new Set());
  const toggle = React2.useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);
  return /* @__PURE__ */ jsx(Fragment, { children: groups.map((group) => /* @__PURE__ */ jsx(CommandGroup, { heading: `${group.label} \xB7 ${group.hits.length}`, children: group.hits.map((hit) => {
    const key = `${hit.kind}:${hit.id}`;
    const chains = chainDrilldown ? hitChains(hit) : [];
    const scopes = scopeDrilldown ? hitScopes(hit).filter(isWidenableScope) : [];
    const open = expanded.has(key);
    const scopeKey = `${key}:scope`;
    const scopesOpen = expanded.has(scopeKey);
    return /* @__PURE__ */ jsxs(React2.Fragment, { children: [
      /* @__PURE__ */ jsx(CommandItem, { value: key, onSelect: () => onSelect(hit), children: /* @__PURE__ */ jsx(SearchHitItem, { hit, config: itemConfig }) }),
      chains.length > 1 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(
          CommandItem,
          {
            value: `${key}:chains`,
            onSelect: () => toggle(key),
            className: "pl-8 text-xs text-muted-foreground",
            children: [
              /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: open ? "\u25BE" : "\u25B8" }),
              open ? "Hide chains" : `Open on a specific chain (${chains.length})`
            ]
          }
        ),
        open && chains.map((chain) => /* @__PURE__ */ jsx(
          CommandItem,
          {
            value: `${key}:${chain}`,
            onSelect: () => onSelect(chainScopedHit(hit, chain)),
            className: "pl-12 text-xs",
            children: /* @__PURE__ */ jsx(ChainAvatar, { chain, size: "sm" })
          },
          chain
        ))
      ] }),
      scopes.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(
          CommandItem,
          {
            value: `${key}:scopes`,
            onSelect: () => toggle(scopeKey),
            className: "pl-8 text-xs text-muted-foreground",
            children: [
              /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: scopesOpen ? "\u25BE" : "\u25B8" }),
              scopesOpen ? "Hide other scopes" : `Also defined at another scope (${scopes.length})`
            ]
          }
        ),
        scopesOpen && scopes.map((scope) => /* @__PURE__ */ jsxs(
          CommandItem,
          {
            value: `${key}:scope:${scope.scope}`,
            onSelect: () => onSelect(scopedHit(hit, scope)),
            className: "pl-12 text-xs",
            children: [
              addressRowScopeLabel(scope.scope),
              scope.count > 1 ? /* @__PURE__ */ jsxs("span", { className: "ml-1 opacity-70", children: [
                "\xD7",
                scope.count
              ] }) : null
            ]
          },
          scope.scope
        ))
      ] })
    ] }, key);
  }) }, group.kind)) });
}
function SearchCommand({
  provider,
  open,
  onOpenChange,
  onSelectHit,
  identity,
  chainId,
  showFacets = true,
  chainDrilldown = true,
  scopeDrilldown = true,
  placeholder = "Search addresses, transactions, entities\u2026",
  emptyMessage = "No matches.",
  label = "Search"
}) {
  const search = useSearch({ provider, chainId });
  const recent = useRecentSearches({ identity });
  const scoped = search.kinds.length > 0;
  const kindCounts = React2.useMemo(() => {
    if (scoped) return void 0;
    const facet = search.facets.find((f) => f.field === "kind");
    if (!facet) return void 0;
    const out = {};
    for (const v of facet.values) out[v.value] = v.count;
    return out;
  }, [search.facets, scoped]);
  const handleSelect = React2.useCallback(
    (hit) => {
      recent.add(search.text);
      onOpenChange(false);
      onSelectHit(hit);
    },
    [recent, search.text, onOpenChange, onSelectHit]
  );
  const { clear } = search;
  React2.useEffect(() => {
    if (!open) clear();
  }, [open, clear]);
  const showRecents = search.text.trim().length < SEARCH_MIN_QUERY_LENGTH;
  const hidden = search.total != null ? search.total - search.hits.length : 0;
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(DialogContent, { className: "max-w-2xl overflow-hidden p-0", showClose: false, children: [
    /* @__PURE__ */ jsx(DialogTitle, { className: "sr-only", children: label }),
    /* @__PURE__ */ jsx(DialogDescription, { className: "sr-only", children: placeholder }),
    /* @__PURE__ */ jsxs(Command, { shouldFilter: false, className: "[&_[cmdk-group-heading]]:px-2", children: [
      /* @__PURE__ */ jsx(CommandInput, { value: search.text, onValueChange: search.setText, placeholder }),
      showFacets && /* @__PURE__ */ jsx(FacetFilters, { selected: search.kinds, onToggle: search.toggleKind, counts: kindCounts }),
      /* @__PURE__ */ jsxs(CommandList, { className: "max-h-[60vh]", children: [
        search.status === "searching" && /* @__PURE__ */ jsxs("div", { className: "space-y-2 p-3", children: [
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-full" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-4/5" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-3/5" })
        ] }),
        search.status === "error" && /* @__PURE__ */ jsx("div", { className: "p-4 text-center text-sm text-destructive", children: "Search failed. Try again." }),
        showRecents && search.status !== "searching" && recent.recents.length === 0 && /* @__PURE__ */ jsx("div", { className: "p-4 text-center text-sm text-muted-foreground", children: "Type to search across addresses, transactions, and entities." }),
        showRecents && recent.recents.length > 0 && /* @__PURE__ */ jsx(CommandGroup, { heading: "Recent", children: recent.recents.map((r) => /* @__PURE__ */ jsx(CommandItem, { value: `recent:${r.text}`, onSelect: () => search.setText(r.text), children: /* @__PURE__ */ jsx("span", { className: "truncate", children: r.text }) }, r.text)) }),
        !showRecents && search.status === "ready" && search.groups.length === 0 && /* @__PURE__ */ jsx(CommandEmpty, { children: emptyMessage }),
        !showRecents && search.groups.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            SearchResults,
            {
              groups: search.groups,
              onSelect: handleSelect,
              chainDrilldown,
              scopeDrilldown
            }
          ),
          hidden > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(CommandSeparator, {}),
            /* @__PURE__ */ jsxs("div", { className: "px-3 py-2 text-center text-xs text-muted-foreground", children: [
              "Showing ",
              search.hits.length,
              " of ",
              search.total,
              " matches \u2014 narrow the query or pick a type."
            ] })
          ] })
        ] })
      ] })
    ] })
  ] }) });
}
var COMBOBOX_PREVIEW = {
  ...DEFAULT_SEARCH_PREVIEW_CONFIG,
  density: "compact",
  hover: false
};
function SearchCombobox({
  provider,
  kinds,
  chainId,
  value,
  onSelectHit,
  placeholder = "Select\u2026",
  searchPlaceholder = "Search\u2026",
  emptyMessage = "No matches.",
  disabled,
  className,
  contentClassName
}) {
  const [open, setOpen] = React2.useState(false);
  const search = useSearch({ provider, initialKinds: kinds, chainId });
  const { setKinds } = search;
  if (useDepsChanged([(kinds ?? []).join(",")])) setKinds(kinds ?? []);
  const handleSelect = React2.useCallback(
    (hit) => {
      setOpen(false);
      onSelectHit(hit);
    },
    [onSelectHit]
  );
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      Button,
      {
        type: "button",
        variant: "outline",
        role: "combobox",
        "aria-expanded": open,
        disabled,
        className: cn("justify-between font-normal", className),
        children: [
          /* @__PURE__ */ jsx("span", { className: cn("truncate", !value && "text-muted-foreground"), children: value ? value.title : placeholder }),
          /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "ml-2 opacity-50", children: "\u2304" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(PopoverContent, { align: "start", className: cn("w-[var(--radix-popover-trigger-width)] p-0", contentClassName), children: /* @__PURE__ */ jsxs(Command, { shouldFilter: false, children: [
      /* @__PURE__ */ jsx(CommandInput, { value: search.text, onValueChange: search.setText, placeholder: searchPlaceholder }),
      /* @__PURE__ */ jsxs(CommandList, { children: [
        search.status === "searching" && /* @__PURE__ */ jsxs("div", { className: "space-y-2 p-2", children: [
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-full" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-3/4" })
        ] }),
        search.status === "idle" && // cmdk renders nothing for an unstarted query, so the dropdown
        // opened as a blank box with no hint that typing is what fills it.
        /* @__PURE__ */ jsxs("div", { className: "px-3 py-4 text-center text-xs text-muted-foreground", children: [
          "Type at least ",
          SEARCH_MIN_QUERY_LENGTH,
          " characters to search."
        ] }),
        search.status === "error" && /* @__PURE__ */ jsx("div", { className: "px-3 py-4 text-center text-xs text-destructive", children: "Search failed. Try again." }),
        search.status === "ready" && search.groups.length === 0 && /* @__PURE__ */ jsx(CommandEmpty, { children: emptyMessage }),
        search.groups.length > 0 && /* @__PURE__ */ jsx(SearchResults, { groups: search.groups, onSelect: handleSelect, itemConfig: COMBOBOX_PREVIEW })
      ] })
    ] }) })
  ] });
}

export { DEFAULT_FIELD_RENDERERS, DEFAULT_SEARCH_PREVIEW_CONFIG, FacetFilters, SEARCH_DEFAULT_LIMIT, SEARCH_MIN_QUERY_LENGTH, SEARCH_PACKAGE, SEARCH_PREVIEW_FIELDS, SEARCH_PREVIEW_FIELD_LABELS, SEARCH_RECENTS_KEY, SEARCH_RECENTS_LIMIT, SEARCH_RECENTS_NAME, SEARCH_RESOURCE_KINDS, SEARCH_TENANT_KIND, SearchCombobox, SearchCommand, SearchHitDetails, SearchHitItem, SearchPreviewProvider, SearchResults, byRelevance, chainScopedHit, createApiSearchProvider, createCompositeSearchProvider, createInMemorySearchProvider, groupHitsByKind, hitAddress, hitChains, hitScopes, hitTxHash, nextRecents, previewFieldLabel, resolveFields, resourceKindLabel, resourceKindOrder, scopedHit, useRecentSearches, useSearch, useSearchPreview, withoutRecent };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map